<!--
@Author: Kleber Pinel <kleberdasilva>
@Date:   2019-03-19T10:16:40-07:00
@Email:  kleber@hackcapital.com
@Last modified by:   kleberdasilva
@Last modified time: 2019-03-25T15:12:14-07:00
-->

<template lang="pug">
  .wrapper(:class="readyOnly")
    slide-drawer(
      :confirm-dismiss="confirmDismiss"
      ref="slide-drawer"
      v-if="showSlideDrawer"
      v-model="showSlideDrawer"
      @input="handleReset"
    )
      template(slot="title") {{ showVideoLibrary ? 'Video Library' : 'Video Details' }}
      template(slot="content")
        video-library(
          @click-ld-video="handleShowVideo"
          @edit-ld-video="handleShowVideo"
          @insert-ld-video="insertLdVideo"
          :communicationType="communicationType"
          v-if="showVideoLibrary"
          ref="videoLibrary"
        )
        chat-video-detail(
          :chatVideo="videoClicked"
          :isVideoEditable="isVideoEditable"
          :showVideoDetail="showVideoDetail"
          @modified:video="confirmDismiss = true"
          is-email-composer=true
          v-if="showVideoDetail"
        )
      template(slot="actions")
        v-btn(
          color="primary"
          outline
          v-if="showVideoDetail && !isVideoEditable"
          @click="handleActivateVideoEdit"
        ) Edit

        v-btn.primary(
          v-if="showVideoDetail && isVideoEditable"
          @click="handleVideoUpdate(videoClicked)"
        ) Save

        v-btn(
          v-if="showVideoDetail && !isVideoEditable" color="primary"
          @click="insertLdVideo(videoClicked)"
        ) Select

    rich-text-editor-toolbar(
      :attachment-handler-id="attachmentHandlerId"
      :disabled="disabled"
      :imageSelection="selectedMarkup"
      :textValue="value"
      :toggleToolbarItemVisible="toggleToolbarItemVisible"
      :tools="allowedTools"
      :toolType="toolType"
      :font="selectedFont"
      :font-size="selectedFontSize"
      :font-color="selectedFontColor"
      :highlight-color="selectedHighlightColor"
      @edit-html="saveNewSource"
      @exec-command="execCommand"
      @handle-video-library="handleVideoLibrary"
      @insert-emoji="insertEmoji"
      @insert-ld-content="insertLdContent"
      @insert-link-url="insertLinkUrl"
      @insert-url-image="insertImageURL"
      @change-font="handleChangeFont"
      @change-font-size="handleChangeFontSize"
      @change-font-color="handleChangeFontColor"
      @change-highlight-color="handleChangeHighlightColor"
    )
    div.richtexteditor(
      data-cy="rich-text-editor"
      placeholder="Insert text here..."
      @input="onUserUpdate"
      @click="onClick"
      @keyup="onKeyup"
      @focus="computeToolState"
      @blur="saveCaretData(editor)"
      :class="{'pre-wrap-format' : communicationType === 'text' || communicationType === 'event' }"
      :contenteditable="!disabled"
      ref="editor"
    )
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import ChatVideoDetail from '@/Text/components/ChatVideoDetail';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import SlideDrawer from '@/Core/components/SlideDrawer';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import TitleBar from '@/Core/components/TitleBar';

import Media from '@/Media/mixins/media';
import VideoLibrary from '@/Media/components/VideoLibrary';

import caretPosition from '../../mixins/caret-positioning';
import RichTextEditorToolbar from './RichTextEditorToolbar';
import RichTextTools from '../../config/rich-text-tools';

import { rgbToHex } from '../../utilities/colorParser';


const {
  mapState: mapVideosState,
  mapActions: mapVideosActions,
} = createNamespacedHelpers('videos');


export default {
  components: {
    ChatVideoDetail,
    LiondeskDialog,
    RichTextEditorToolbar,
    SlideDrawer,
    TitleBar,
    VideoLibrary,
  },
  props: {
    toolType: {
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    value: {
      default: null,
      type: String,
    },
    attachmentHandlerId: {
      type: String,
      default: '',
    },
    communicationType: {
      default: 'email',
      type: String,
    },
    toggleToolbarItem: Boolean,
  },
  mixins: [caretPosition, Media, toggleSlideDrawer],
  data() {
    return {
      // each editor instance gets its own copy of tools
      editor: null,
      selectedMarkup: null,
      toggleToolbarItemVisible: false,
      tools: RichTextTools.map(tool => Object.assign({ isActive: false }, tool)),
      selectedFont: null,
      selectedFontSize: null,
      selectedFontColor: null,
      selectedHighlightColor: null,
    };
  },
  mounted() {
    this.editor = this.$refs.editor;
    this.editor.innerHTML = this.value;
    this.saveCaretData(this.editor);
  },
  watch: {
    value(newValue) {
      // Only update if non-editable or input is not focused
      if (this.disabled || document.activeElement !== this.editor) {
        // sync editor with state
        this.editor.innerHTML = newValue;
      }
      this.saveCaretData(this.editor);
    },
    toggleToolbarItem(newValue) {
      this.close();
    },
  },
  computed: {
    ...mapVideosState(['videos']),
    readyOnly() {
      return this.disabled ? 'read-only' : '';
    },
    allowedTools() {
      return this.tools.filter(tool => !this.toolType || tool.typeFor.includes(this.toolType));
    },
  },
  methods: {
    ...mapVideosActions(['editVideo', 'findVideos']),
    handleVideoUpdate(videoUpdated) {
      this.editVideo({ video: videoUpdated });
      this.closeSlideDrawer(false);

      const query = {
        $skip: (this.paginationGetPage - 1) * this.paginationGetPageLimit,
        $limit: this.paginationGetPageLimit,
        communication_type: 'email',
        $sort: { created_at: -1 },
      };
      this.findVideos(query);
    },
    handleReset() {
      if (this.$refs.videoLibrary) {
        this.$refs.videoLibrary.reset();
      }
    },
    insertLdVideo(selectedVideo) {
      const video = this.formatInsertVideo(selectedVideo);
      this.insertInCurrentValue(video);

      this.closeSlideDrawer(false);
    },
    insertInCurrentValue(newTextToBeInserted) {
      this.restoreCaretPosition(this.editor);
      this.execCommand('insertText', newTextToBeInserted);
    },
    onKeyup(e) {
      this.resetImageSelection();
      this.computeToolState(e);
    },
    onClick(e) {
      this.resetImageSelection();
      if (e.target.nodeName === 'IMG' && !this.disabled) {
        e.target.classList.add('img-editing-active');
        const selection = window.getSelection();
        selection.removeAllRanges();
        const range = document.createRange();
        range.selectNode(e.target);
        selection.addRange(range);
        this.selectedMarkup = e.target.outerHTML;
      }
      this.computeToolState(e);
    },
    resetImageSelection() {
      this.selectedMarkup = '';
      this.editor.querySelectorAll('img').forEach(el => {
        el.classList.remove('img-editing-active');
      });
    },
    computeToolState(e) {
      if (this.editor.contains(window.getSelection().focusNode)) {
        const parent = window.getSelection().focusNode.parentElement;
        const style = window.getComputedStyle(parent);
        this.tools.forEach(tool => {
          tool.isActive = tool.getActive(style, parent);
        });

        this.selectedFont = style.fontFamily;
        this.selectedFontSize = style.fontSize;
        this.selectedFontColor = this.getColor(style.color);
        const backgroundColor = this.getBackgroundColor(parent);
        this.selectedHighlightColor = this.getColor(backgroundColor);
      }
    },
    onUserUpdate(value) {
      this.resetImageSelection();
      if (typeof value === 'object') {
        this.$emit('input', this.editor.innerHTML);
        return;
      }
      this.$emit('input', value);
    },
    saveNewSource(sourceEditing) {
      this.onUserUpdate(sourceEditing);
      this.close();
    },
    insertLdContent(selectedValue) {
      this.insertInCurrentValue(selectedValue);
      this.close();
    },
    insertEmoji(emoji) {
      this.insertInCurrentValue(emoji);
      this.close();
    },
    insertImageURL(imageData) {
      this.restoreCaretPosition();
      const selectedImage = this.editor.querySelector('.img-editing-active');
      if (selectedImage) {
        selectedImage.src = imageData.src;
        if (imageData.width) {
          selectedImage.width = imageData.width;
        }
        if (imageData.height) {
          selectedImage.height = imageData.height;
        }
        selectedImage.classList.remove('img-editing-active');
        this.addImageHyperlink(imageData, selectedImage);
        this.selectedMarkup = '';
        this.onUserUpdate(this.editor.innerHTML);
      } else {
        const newImage = new Image();
        newImage.src = imageData.src;
        newImage.width = imageData.width;
        newImage.height = imageData.height;
        if (imageData.title) {
          this.addImageHyperlink(imageData, newImage);
        } else {
          this.execCommand('insertHtml', newImage.outerHTML);
        }
      }
      this.close();
    },

    addImageHyperlink(imageResponse, selectedImage) {
      const imageTitleChange = imageResponse.title !== selectedImage.title;
      const imageHasParent = selectedImage.parentElement;
      const hyperlinkImage =
        `<a href='${imageResponse.title}' target="_blank">${imageResponse.outerHTML}</a>`;

      if (!imageHasParent) {
        return this.execCommand('insertHtml', hyperlinkImage);
      }

      if (imageTitleChange) {
        // The image hyperlink is added
        if (imageResponse.title) {
          selectedImage.parentElement.insertAdjacentHTML('afterend', hyperlinkImage);
          selectedImage.remove();
        }
        // The image hyperlink is removed
        if (selectedImage.title) {
          selectedImage.parentElement.remove();
          this.execCommand('insertHtml', imageResponse.outerHTML);
        }
      }
    },

    insertLinkUrl(linkText, linkValue) {
      this.restoreCaretPosition();
      if (linkText === '') {
        const link = `<a href='${linkValue}' target="_blank">Link</a>`;
        this.execCommand('insertHtml', link);
      } else {
        this.execCommand('createLink', linkValue);
        let link = document.getSelection().focusNode.parentElement;
        link.target = '_blank';
        
        this.onUserUpdate(this.editor.innerHTML);
      }

      this.close();
    },

    execCommand(command, value) {
      document.execCommand(command, false, value);
      this.editor.focus();
    },

    handleChangeFont(value) {
      this.execCommand('fontName', value);
      this.editor.querySelectorAll('font[face]').forEach(elem => {
        elem.removeAttribute('face');
        elem.style.fontFamily = value;
      });

      this.selectedFont = value;
      this.onUserUpdate(this.editor.innerHTML);
    },
    
    handleChangeFontSize(value) {
      this.execCommand('fontSize', value);
      this.editor.querySelectorAll('font[size]').forEach(elem => {
        elem.removeAttribute('size');
        elem.style.fontSize = value;
      });

      this.selectedFontSize = value;
      this.onUserUpdate(this.editor.innerHTML);
    },
    
    handleChangeFontColor(value) {
      this.restoreCaretPosition();

      this.execCommand('foreColor', value);
      this.editor.querySelectorAll('font[color]').forEach(elem => {
        elem.removeAttribute('color');
        elem.style.color = value;
      });

      this.selectedFontColor = value;
      this.onUserUpdate(this.editor.innerHTML);
    },

    handleChangeHighlightColor(value) {
      this.restoreCaretPosition();
      this.execCommand('hiliteColor', value);
      this.selectedHighlightColor = value;
    },

    getBackgroundColor(element) {
      const defaultColor = "rgba(0, 0, 0, 0)";

      var backgroundColor = window.getComputedStyle(element).backgroundColor;
      if (backgroundColor != defaultColor) return backgroundColor;

      if (!element.parentElement || element === this.editor) return defaultColor;
      return this.getBackgroundColor(element.parentElement);
    },

    // expect a string sepparated by commas with the values
    getColor(rgbColor) {
      rgbColor.replaceAll(" ", "");
      const start = rgbColor.indexOf("(");
      const end = rgbColor.indexOf(")");
      let color = rgbColor;
      if (start >= 0 && end >= 0) {
        color = rgbColor.substring(start + 1, end);
      }
      const [r, g, b] = color.split(',');
      return rgbToHex(Number(r),Number(g),Number(b));
    },

    close() {
      this.toggleToolbarItemVisible = !this.toggleToolbarItemVisible;
    },
  },
};
</script>

<style lang="scss" scoped>
[contenteditable='true']:empty:before {
  content: attr(placeholder);
  display: block; /* For Firefox */
  font-style: italic;
  color: var(--v-background-lighten2);
}
.wrapper {
  border: solid 1px rgba(0,0,0,0.35);
  &.read-only {
    border-color: rgba(0,0,0,0.1);
  }
}
.richtexteditor {
  font-family: sans-serif;
  padding: 8px;
  outline: none;
  white-space: initial;
  overflow: auto;
  min-height: 200px;
}
.richtexteditor /deep/ blockquote {
  margin: 0 0 0 40px; // FireFox indents
}
.editor-tools /deep/ .v-toolbar__content {
  padding: 0 4px;
}
.richtexteditor /deep/ .img-editing-active {
  opacity: 0.75;
}
.pre-wrap-format {
  white-space: pre-wrap !important;
}
</style>
